import { getSelected } from '~/containers/FilterContainer/pureFunctions';
import type {
  MenuItem,
  SelectedMenuItem,
  FetchIngredientsPayload,
  MenuValues,
  FilterAnimate,
} from '~/containers/FilterContainer/types';
import { defineStore } from 'pinia';
import { wrapPiniaStore } from '@devhacker/shared/utils/wrapPiniaStore';
import { ref, reactive, computed } from 'vue';

export const useFilterStore = wrapPiniaStore(
  defineStore('filter', () => {
    const { $recipesApi } = useNuxtApp();
    const dishes = ref<MenuItem[]>([]);
    const recipesCount = ref(0);
    const isAnimate = ref(true);
    const isFixedSubmitButton = ref(true);
    const openedMenu = ref<MenuValues | ''>('');
    const animate = reactive<FilterAnimate>({
      dishes: true,
      cuisines: true,
      includedIngredients: true,
      excludedIngredients: true,
    });
    const sortDirection = ref<'asc' | 'desc'>('asc');
    const dishesTwice = ref(false);

    const selectedDishes = computed(() => dishes.value.filter(getSelected) as SelectedMenuItem[]);

    async function fetchFilterSettings(path: string) {
      try {
        return await $recipesApi.fetchFilterSettings(path);
      } catch (error) {
        console.error(error);
        return null;
      }
    }

    async function fetchRecipesCount(url: string) {
      try {
        const { count } = await $recipesApi.fetchRecipesCount(url);
        recipesCount.value = count || 0;
      } catch (error: any) {
        recipesCount.value = 0;
        console.error('fetch recipes count error: ', error);
      }
    }

    async function fetchMenus() {
      try {
        return await $recipesApi.fetchFilterMenus();
      } catch (error) {
        console.error(error);
        return null;
      }
    }

    async function fetchIngredients({ searchValue, type }: FetchIngredientsPayload) {
      try {
        const { items = [] } = await $recipesApi.searchIngredients(searchValue);
        return { items, type };
      } catch (error) {
        console.error(error);
      }
    }

    function actionHandlerPhone() {
      animate.dishes = true;
      animate.cuisines = true;
      animate.includedIngredients = true;
      animate.excludedIngredients = true;
      dishesTwice.value = true;
      sortDirection.value = 'asc';
    }

    function actionHandlerTablet() {
      animate.dishes = true;
      animate.cuisines = true;
      animate.includedIngredients = true;
      animate.excludedIngredients = true;
      dishesTwice.value = false;
      sortDirection.value = 'asc';
    }

    function actionHandlerDesktop() {
      animate.dishes = true;
      animate.cuisines = false;
      animate.includedIngredients = false;
      animate.excludedIngredients = false;
      dishesTwice.value = false;
      sortDirection.value = 'desc';
    }

    function setOpenedMenu(menu: MenuValues | '') {
      openedMenu.value = menu;
    }

    function setIsFixedSubmitButton(value: boolean) {
      isFixedSubmitButton.value = value;
    }

    function setRecipesCount(count: number) {
      recipesCount.value = count;
    }

    return {
      // state
      dishes,
      recipesCount,
      isAnimate,
      isFixedSubmitButton,
      openedMenu,
      animate,
      sortDirection,
      dishesTwice,
      // getters
      selectedDishes,
      // actions
      fetchFilterSettings,
      fetchRecipesCount,
      fetchMenus,
      fetchIngredients,
      actionHandlerPhone,
      actionHandlerTablet,
      actionHandlerDesktop,
      setOpenedMenu,
      setIsFixedSubmitButton,
      setRecipesCount,
    };
  }),
);
